import { useState, type Dispatch, type SetStateAction } from "react";

import {
  usePick,
  useTaxonomy,
  type PickBucket,
  type PickItem,
  type PickQuestion,
  type TaxonomyEntryOut,
} from "@/api/queries";
import { Button } from "@/components/ui/button";
import { FieldLabel, Input, Label } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, PaneHeader, Panel, PanelHeader } from "@/components/ui/panel";
import { cn } from "@/lib/utils";

/** §17.4 — the order a set is asked in. Project domain first; the Interviewer prepends its
 * own opener, which is what told it which domain to ask for. */
const BUCKETS = ["project_domain", "technical", "problem_solving", "soft_skill"] as const;
type Bucket = (typeof BUCKETS)[number];

const DEFAULTS: Record<Bucket, number> = {
  project_domain: 3,
  technical: 6,
  problem_solving: 2,
  soft_skill: 2,
};

/** Which taxonomy stacks belong to a bucket — the same derivation the picker does server
 * side (§17.2), so the dropdowns only offer what that bucket can actually serve. */
function stacksFor(entries: TaxonomyEntryOut[] | undefined, bucket: Bucket) {
  return (entries ?? []).filter((e) => e.bucket === bucket).map((e) => e.path);
}

/** A multi-select as toggle chips. Combobox is single-value, and §17.3 asks for stacks[]
 * and domains[] — the same facet-row shape the Questions filter bar uses. */
function ChipRow({
  options,
  selected,
  onToggle,
  empty,
}: {
  options: string[];
  selected: string[];
  onToggle: (v: string) => void;
  empty: string;
}) {
  if (options.length === 0) return <p className="text-bank-muted py-1 text-xs">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <button
            key={o}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(o)}
            className={cn(
              "rounded border px-2 py-1 font-mono text-[11px] transition-colors",
              on
                ? "border-bank-accent bg-bank-accent-wash text-bank-accent-ink"
                : "border-bank-border text-bank-muted hover:border-bank-border-strong",
            )}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

function QuestionBlock({ q, follow }: { q: PickQuestion; follow?: boolean }) {
  return (
    <div className={cn(follow && "border-bank-border ml-4 border-l pl-3")}>
      <p className="text-sm">{q.question_text}</p>
      <div className="text-bank-muted mt-1 flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px]">
        <span>#{q.question_id}</span>
        <span>d{q.difficulty_score ?? "—"}</span>
        <span>{q.stack_path ?? "—"}</span>
        {q.primary_concept && <span>{q.primary_concept}</span>}
        {q.question_type && <span>{q.question_type}</span>}
      </div>
      {q.model_answer && (
        <details className="mt-1.5">
          <summary className="text-bank-muted cursor-pointer text-xs">model answer &amp; rubric</summary>
          <p className="border-bank-border bg-bank-surface-2 mt-1.5 rounded-md border px-2.5 py-2 text-xs">
            {q.model_answer}
          </p>
          {q.rubric && q.rubric.length > 0 && (
            <ul className="text-bank-muted mt-1.5 list-disc pl-5 text-xs">
              {q.rubric.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          )}
        </details>
      )}
    </div>
  );
}

function BucketPanel({ bucket }: { bucket: PickBucket }) {
  return (
    <Panel>
      <PanelHeader
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs">{bucket.bucket}</span>
            <span className="text-bank-muted text-xs">
              {bucket.returned} of {bucket.requested}
            </span>
          </span>
        }
      />
      <div className="space-y-3 p-4">
        {/* §17.7 — a short set must never be silent. */}
        {bucket.relaxed && (
          <Callout tone="attention">
            Relaxed: the filter had to widen, or the bank could not fill this bucket. Returned{" "}
            {bucket.returned} of {bucket.requested}.
          </Callout>
        )}
        {bucket.items.length === 0 ? (
          <Empty>No questions in this bucket yet.</Empty>
        ) : (
          bucket.items.map((it: PickItem) => (
            <div key={it.question.question_id} className="space-y-2">
              <div className="flex gap-2">
                <span className="text-bank-muted w-6 shrink-0 pt-0.5 font-mono text-xs">
                  {it.sequence_number}.
                </span>
                <div className="min-w-0 flex-1 space-y-2">
                  <QuestionBlock q={it.question} />
                  {it.follow_ups.map((f) => (
                    <QuestionBlock key={f.question_id} q={f} follow />
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

/**
 * A console for Engine 2 — DESIGN §17. This screen is standing in for the external
 * Interviewer engine: it sends exactly the request that engine sends, to the one
 * authenticated route in the app, so what you see here is what that integration gets.
 */
export default function Picker() {
  const [apiKey, setApiKey] = useState("");
  const [stacks, setStacks] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [experience, setExperience] = useState("4");
  const [followUps, setFollowUps] = useState("2");
  const [counts, setCounts] = useState<Record<Bucket, number>>(DEFAULTS);

  const { data: taxonomy } = useTaxonomy("stack");
  const pick = usePick();

  const technicalStacks = stacksFor(taxonomy, "technical");
  const domainStacks = stacksFor(taxonomy, "project_domain");

  // Functional updater, not `[...stacks, v]`: two toggles in one tick both read the same
  // captured array and the second silently discards the first.
  function toggle(set: Dispatch<SetStateAction<string[]>>, v: string) {
    set((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  }

  function run() {
    if (!apiKey.trim()) return;
    pick.mutate({
      apiKey: apiKey.trim(),
      buckets: Object.fromEntries(BUCKETS.map((b) => [b, counts[b]]).filter(([, n]) => (n as number) > 0)),
      stacks: stacks.map((path) => ({ path, weight: null })),
      domains: domains.map((path) => ({ path, weight: null })),
      experience_years: experience === "" ? null : Number(experience),
      follow_ups_per_technical: followUps === "" ? null : Number(followUps),
      exclude_question_ids: [],
    });
  }

  const result = pick.data;

  return (
    <div className="flex flex-col gap-3 p-4">
      <h1 className="text-base font-semibold tracking-tight">Picker console</h1>
      <Callout>
        Sends the same request the external Interviewer engine sends to <code>POST /pick</code> — the one
        authenticated route here. A pick makes no model call and stores nothing (§17.1).
      </Callout>

      <Panel>
        <PanelHeader title="Request" />
        <div className="space-y-4 p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-56 flex-1">
              <FieldLabel htmlFor="pick-key">Service key (X-API-Key)</FieldLabel>
              <Input
                id="pick-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="service_auth.api_key"
                onKeyDown={(e) => e.key === "Enter" && run()}
              />
            </div>
            <div className="w-28">
              <FieldLabel htmlFor="pick-exp">Experience (yrs)</FieldLabel>
              <Input
                id="pick-exp"
                type="number"
                min={0}
                max={60}
                value={experience}
                onChange={(e) => setExperience(e.target.value)}
              />
            </div>
            <div className="w-32">
              <FieldLabel htmlFor="pick-follow">Follow-ups each</FieldLabel>
              <Input
                id="pick-follow"
                type="number"
                min={0}
                max={5}
                value={followUps}
                onChange={(e) => setFollowUps(e.target.value)}
              />
            </div>
          </div>

          <div>
            <PaneHeader title="Counts per bucket" />
            <div className="mt-2 flex flex-wrap gap-3">
              {BUCKETS.map((b) => (
                <div key={b} className="w-40">
                  <Label htmlFor={`count-${b}`}>{b}</Label>
                  <Input
                    id={`count-${b}`}
                    type="number"
                    min={0}
                    max={50}
                    value={counts[b]}
                    onChange={(e) => setCounts((prev) => ({ ...prev, [b]: Number(e.target.value) }))}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <PaneHeader title="Tech stacks" />
            <div className="mt-2">
              <ChipRow
                options={technicalStacks}
                selected={stacks}
                onToggle={(v) => toggle(setStacks, v)}
                empty="No technical stacks in the taxonomy."
              />
            </div>
          </div>

          <div>
            <PaneHeader title="Project domains" />
            <div className="mt-2">
              <ChipRow
                options={domainStacks}
                selected={domains}
                onToggle={(v) => toggle(setDomains, v)}
                empty="No project domains yet — add one on the Stacks page with bucket 'project_domain'."
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button variant="primary" disabled={pick.isPending || !apiKey.trim()} onClick={run}>
              {pick.isPending ? "Picking…" : "Pick"}
            </Button>
            {result && <span className="text-bank-muted text-xs">{result.total} questions returned</span>}
          </div>
          <ErrorNote error={pick.error} />
        </div>
      </Panel>

      {result?.buckets.map((b) => <BucketPanel key={b.bucket} bucket={b} />)}
    </div>
  );
}
